<script setup lang="ts">
import Popover from 'primevue/popover'
import { computed, ref, useId } from 'vue'
import { useRouter } from 'vue-router'

import { useI18n, type MessageKey } from '@/app/i18n'
import { navTarget, type NavItem } from '@/app/nav/collect'
import { ROUTE_PROFILE } from '@/app/router/names'
import { logout } from '@/app/session'
import { useAuthStore } from '@/app/stores/auth'
import type { SoftwareCode } from '@/app/stores/session'
import type { DropdownAction } from '@/shared/ui/AppDropdownActions.vue'
import AppAvatar from '@/shared/ui/AppAvatar.vue'
import AppIcon from '@/shared/ui/AppIcon.vue'
import { personInitials } from '@/shared/utils/initials'

import AccountSwitchModal from './AccountSwitchModal.vue'
import { helpActions } from './help-actions'
import { useAccountSwitch } from './useAccountSwitch'

/**
 * The account card at the bottom of the vertical nav (wp/shell §4.17 / §4.16b, redesigned 2026-10): avatar, name and
 * status ("Business owner", …), opening a menu above it — the `account` nav entries (organization profile, settings,
 * ACL-filtered like the menu), the profile, the other accounts of the same login ("Switch current role", with the
 * password confirmation), the help entries and the logout. Collapsed, the avatar alone opens the same menu. On
 * desktop this replaces the navbar's user menu, role switcher and help menu.
 */
const props = defineProps<{ items: NavItem[]; software: SoftwareCode; mini: boolean }>()
const { t } = useI18n()
const router = useRouter()
const auth = useAuthStore()
const switcher = useAccountSwitch()
const popover = ref<InstanceType<typeof Popover> | null>(null)
const open = ref(false)
const menuId = `account-menu-${useId()}`

const profile = computed(() => auth.session?.profile)
const name = computed(() => {
  const first = typeof profile.value?.FirstName === 'string' ? profile.value.FirstName : ''
  const last = typeof profile.value?.LastName === 'string' ? profile.value.LastName : ''
  return (
    [first, last].filter(Boolean).join(' ') || (typeof profile.value?.Label === 'string' ? profile.value.Label : '')
  )
})
const initials = computed(() => personInitials(profile.value?.FirstName, profile.value?.LastName))
const workspace = computed(() => auth.currentWorkspace?.Label ?? null)
const help = computed(() => helpActions(t))
// Titles are message keys declared with keys.t() in the nav files.
// eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
const label = (item: NavItem): string => t(item.title as MessageKey)

function toggle(event: Event): void {
  popover.value?.toggle(event)
}

function go(item: NavItem): void {
  popover.value?.hide()
  const target = navTarget(item, props.software)
  if (target) void router.push(target)
}

function run(action: DropdownAction): void {
  popover.value?.hide()
  if (action.href)
    window.open(action.href, action.target ?? '_self', action.target === '_blank' ? 'noopener' : undefined)
  else action.onSelect?.()
}

function switchTo(userId: number): void {
  popover.value?.hide()
  switcher.open(userId)
}

function openProfile(): void {
  popover.value?.hide()
  void router.push({ name: ROUTE_PROFILE })
}

function signOut(): void {
  popover.value?.hide()
  void logout()
}
</script>

<template>
  <div class="nav-account" :class="{ 'is-mini': mini }" data-testid="nav-account">
    <button
      type="button"
      class="nav-account__card"
      :aria-label="mini ? t('shell.navbar.action.userMenu') : undefined"
      aria-haspopup="menu"
      :aria-expanded="open"
      :aria-controls="open ? menuId : undefined"
      data-testid="nav-account-open"
      @click="toggle"
    >
      <AppAvatar :src="profile?.ImageUrl ?? null" :text="initials" :size="40" class="nav-account__avatar" />
      <span class="nav-account__text">
        <span class="nav-account__name" data-testid="nav-account-name">{{ name }}</span>
        <span v-if="switcher.currentLabel.value" class="nav-account__status">{{ switcher.currentLabel.value }}</span>
      </span>
      <AppIcon name="ChevronsUpDownIcon" :size="16" class="nav-account__chevron" />
    </button>

    <Popover
      ref="popover"
      :base-z-index="1100"
      :pt="{ root: { class: 'shell-popover nav-account__panel', 'data-testid': 'nav-account-menu' } }"
      @show="open = true"
      @hide="open = false"
    >
      <div :id="menuId" role="menu" :aria-label="t('shell.navbar.action.userMenu')" class="nav-account__menu">
        <div class="nav-account__head">
          <AppAvatar :src="profile?.ImageUrl ?? null" :text="initials" :size="40" />
          <span class="nav-account__text">
            <span class="nav-account__name">{{ name }}</span>
            <span class="nav-account__status">
              {{ [switcher.currentLabel.value, workspace].filter(Boolean).join(' · ') }}
            </span>
          </span>
        </div>

        <template v-if="items.length">
          <hr class="nav-account__rule" />
          <button
            v-for="item in items"
            :key="item.id"
            type="button"
            role="menuitem"
            class="nav-account__item"
            :data-nav="item.id"
            @click="go(item)"
          >
            <AppIcon :name="item.icon || 'CircleIcon'" :size="18" />
            <span>{{ label(item) }}</span>
          </button>
        </template>

        <hr class="nav-account__rule" />
        <button type="button" role="menuitem" class="nav-account__item" data-action="profile" @click="openProfile">
          <AppIcon name="UserIcon" :size="18" />
          <span>{{ t('common.nav.profile') }}</span>
        </button>
        <template v-if="switcher.options.value.length">
          <div class="nav-account__heading">{{ t('shell.navbar.hint.switchRole') }}</div>
          <button
            v-for="option in switcher.options.value"
            :key="option.id"
            type="button"
            role="menuitem"
            class="nav-account__item"
            :data-action="option.id"
            @click="switchTo(option.userId)"
          >
            <AppIcon :name="option.icon" :size="18" />
            <span>{{ option.label }}</span>
          </button>
        </template>

        <hr class="nav-account__rule" />
        <button
          v-for="action in help"
          :key="action.id"
          type="button"
          role="menuitem"
          class="nav-account__item"
          :data-action="action.id"
          @click="run(action)"
        >
          <AppIcon :name="action.icon || 'HelpCircleIcon'" :size="18" />
          <span>{{ action.label }}</span>
        </button>

        <hr class="nav-account__rule" />
        <button
          type="button"
          role="menuitem"
          class="nav-account__item is-danger"
          data-action="logout"
          data-testid="nav-account-logout"
          @click="signOut"
        >
          <AppIcon name="LogOutIcon" :size="18" />
          <span>{{ t('common.action.logout') }}</span>
        </button>
      </div>
    </Popover>

    <AccountSwitchModal :switcher="switcher" />
  </div>
</template>

<style scoped>
.nav-account {
  padding: 0.5rem 0.75rem 0.75rem;
}
.nav-account.is-mini {
  display: flex;
  justify-content: center;
  padding: 0.5rem 0 0.75rem;
}
.nav-account__card {
  display: flex;
  gap: 0.7rem;
  align-items: center;
  width: 100%;
  padding: 0.43rem;
  font: inherit;
  text-align: left;
  cursor: pointer;
  background: var(--p-surface-0);
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  transition:
    border-color 0.2s ease,
    background 0.2s ease;
}
.nav-account__card:hover,
.nav-account__card[aria-expanded='true'] {
  background: var(--p-surface-50);
  border-color: var(--p-surface-300);
}
.nav-account__card:focus-visible {
  outline: 2px solid var(--p-primary-300);
  outline-offset: 2px;
}
.nav-account.is-mini .nav-account__card {
  justify-content: center;
  width: auto;
}
.nav-account.is-mini .nav-account__text,
.nav-account.is-mini .nav-account__chevron {
  display: none;
}
.nav-account__avatar {
  flex: 0 0 auto;
}
.nav-account__text {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}
.nav-account__name {
  overflow: hidden;
  font-size: 1rem;
  font-weight: 600;
  color: var(--p-surface-950);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nav-account__status {
  overflow: hidden;
  font-size: 0.929rem;
  font-weight: 500;
  color: var(--p-surface-600);
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nav-account__chevron {
  flex: 0 0 auto;
  color: var(--p-surface-500);
}
.nav-account__menu {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.nav-account__head {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  padding: 0.5rem 0.75rem 0.6rem;
}
.nav-account__rule {
  margin: 0.3rem 0.25rem;
  border: 0;
  border-top: 1px solid var(--p-content-border-color);
}
.nav-account__heading {
  padding: 0.5rem 0.75rem 0.2rem;
  font-size: 0.786rem;
  font-weight: 600;
  color: var(--p-surface-500);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}
.nav-account__item {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  height: 40px;
  padding: 0 0.75rem;
  font: inherit;
  font-weight: 500;
  color: var(--p-text-color);
  text-align: left;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: calc(var(--p-border-radius-lg) - 2px);
}
.nav-account__item:hover,
.nav-account__item:focus-visible {
  background: var(--p-surface-100);
  outline: none;
}
.nav-account__item.is-danger {
  font-weight: 600;
  color: var(--p-danger-500);
}
</style>

<style>
/* The menu panel is teleported to the body; its frame is the shell's `.shell-popover` (theme/tailwind.css). It opens
   above the card (the card sits at the bottom): the offset goes up, not down. */
.nav-account__panel {
  --shell-popover-width: 300px;
  --shell-popover-offset: -0.4rem;
}
</style>
